import 'driver.js/dist/driver.css';
import './Game.css';
import './tutorial.css';
import { driver } from 'driver.js';
import feedbackExample from './assets/feedback_example.jpg';
import { DifficultyLevel, TurnType } from './State.tsx';
import { track } from './umami.ts';

const preGameExitPrompt = () =>
    confirm('Are you sure you want to skip this tour and go directly to the practice puzzle?');

const midGameExitPrompt = () => confirm('Are you sure you want to skip this tour and return to the puzzle?');

const postGameExitPrompt = () => confirm('Are you sure you want to skip this tour?');

const createPreGameDriver = function (onDestroyedCallback) {
    const preGameDriver = driver({
        allowClose: true,
        onHighlightStarted: (elem, step, options) => {
            if (options.state.activeIndex === 0) {
                document.documentElement.setAttribute('tour-mode', 'pre-game');
            }
            document.documentElement.setAttribute('driver-step', options.state.activeIndex.toString());
        },
        onDestroyed: () => {
            document.documentElement.removeAttribute('tour-mode');
            document.documentElement.removeAttribute('driver-step');
            onDestroyedCallback();
        },
        onDestroyStarted: () => {
            if (!preGameDriver.hasNextStep() || preGameExitPrompt()) {
                if (preGameDriver.hasNextStep()) {
                    track('early_exit_tutorial_pre_game');
                }
                preGameDriver.destroy();
            }
        },
        stagePadding: 2,
        popoverClass: 'tutorial-theme',
        overlayColor: 'var(--tutorial-overlay-color)',
        steps: [
            {
                popover: {
                    description: `Welcome to <b>Triplit</b>! Let's learn to play by solving a practice-round puzzle together.`,
                },
            },
            {
                element: '.triplet-container',
                popover: { description: 'You start with these <b>three</b> letters at the top.' },
            },
            {
                element: '.guesses-container',
                popover: {
                    description: `Your <b>goal</b> is to figure out a 4-, 5-, and 6-letter word.
                        <p>Each word must contain <b>all</b> the letters from the row above it, plus <b>one new</b> letter.</p>`,
                },
            },
            {
                element: '.guess-row:nth-of-type(2)',
                popover: {
                    side: 'bottom',
                    description:
                        'So for example, the 4-letter word will contain the letters <b>T</b>, <b>S</b>, and <b>E</b>, plus one new letter!',
                },
            },
            {
                element: '.outer-clue-container',
                popover: {
                    description: `You also get <b>three</b> clues for each of these three words! <p>These clues are in <b>random</b> order 🔀. You have to figure out which word goes with which clue!</p>`,
                },
            },
            {
                element: '.outer-clue-container',
                popover: {
                    description: `Let's figure out one word together. Cycle through the clues until you reach the one that says "<b>Dines</b>"`,
                },
            },
            {
                element: '.outer-clue-container',
                popover: {
                    description:
                        "Can you think of a word that means 'Dines' and contains the letters <b>T</b>, <b>S</b>, and <b>E</b>?<p>Note that 'Dines' is a <b>verb</b>, so the answer must <b>also</b> be a verb.</p>",
                },
            },
            {
                element: '.outer-clue-container',
                popover: {
                    description: 'Yes, the answer is <b>EATS</b>! Type it in to the first row!',
                },
            },
        ],
    });
    return preGameDriver;
};

const createMidGameDriver = function (onDestroyedCallback) {
    const midGameDriver = driver({
        allowClose: false,
        onDestroyed: onDestroyedCallback,
        onDestroyStarted: () => {
            if (!midGameDriver.hasNextStep() || midGameExitPrompt()) {
                if (midGameDriver.hasNextStep()) {
                    track('early_exit_tutorial_first_word');
                }
                midGameDriver.destroy();
            }
        },
        stagePadding: 2,
        popoverClass: 'tutorial-theme',
        overlayColor: 'var(--tutorial-overlay-color)',
        steps: [
            {
                popover: {
                    description: `Great job filling in your <b>first word</b>! 🎉
                    <p>Try to guess the remaining two words from the other two clues, <b>then</b> hit submit! You will get feedback, so wild guesses work just fine! </p>`,
                },
            },
        ],
    });
    return midGameDriver;
};

const createFirstIncorrectTurnDriver = function (turnType: TurnType, partialSubmit: boolean, onDestroyedCallback) {
    const description = `
        You just ended your first turn. You now have <b>4</b> turns remaining to solve the puzzle!
    ${partialSubmit ? `Keep in mind you can submit <b>all words at once</b> to conserve turns!` : ``} ${
        turnType === TurnType.REVEAL
            ? `<p>Since you used <b>Reveal</b>, only <b>one</b> tile has been revealed to you. When you <b>Submit</b>, you will get feedback on all your guesses!`
            : `<p>You will now get <b>feedback</b> on your guesses!`
    } For example, here everything is correct besides the <b>M</b> and the <b>I</b>:
                    <div><img class='feedback-example' src='${feedbackExample}'/></div>`;

    const firstIncorrectSubmitDriver = driver({
        allowClose: true,
        onDestroyed: onDestroyedCallback,
        onDestroyStarted: () => {
            if (firstIncorrectSubmitDriver.hasNextStep()) {
                track('early_exit_tutorial_first_incorrect_turn');
            }
            firstIncorrectSubmitDriver.destroy();
        },
        stagePadding: 2,
        popoverClass: 'tutorial-theme',
        overlayColor: 'var(--tutorial-overlay-color)',
        steps: [
            {
                popover: {
                    description: description,
                },
            },
            ...(turnType === TurnType.SUBMIT
                ? [
                      {
                          element: '.control-row',
                          popover: {
                              description: `PS: If you get really stuck, you can use <b>Reveal</b> to reveal a letter in a word of your choice, but this will use up a turn!</p>`,
                          },
                      },
                  ]
                : []),
        ],
    });
    return firstIncorrectSubmitDriver;
};

function createPostGameDriver(score: number, onDestroyedCallback: () => void) {
    const successDescription = `Great job! 🎉 You solved the puzzle in <b>${score} turn${score > 1 ? 's' : ''}</b>!`;
    const failureDescription = `Nice effort! But you ran out of turns: you only get <b>5 turns</b> to solve the puzzle. Remember, you can submit multiple words at once, which will help you conserve turns!`;

    const postGameDriver = driver({
        allowClose: true,
        onDestroyed: () => onDestroyedCallback(),
        stagePadding: 2,
        popoverClass: 'tutorial-theme',
        onDestroyStarted: () => {
            if (!postGameDriver.hasNextStep() || postGameExitPrompt()) {
                if (postGameDriver.hasNextStep()) {
                    track('early_exit_tutorial_post_game');
                }
                track('tutorial_completed_' + (score === -1 ? 'failure' : 'success'));
                postGameDriver.destroy();
            }
        },
        overlayColor: 'var(--tutorial-overlay-color)',
        steps: [
            {
                element: '.guesses-container',
                popover: {
                    description: score > 0 ? successDescription : failureDescription,
                },
            },
            {
                element: '.guesses-container',
                popover: {
                    description: `When you complete the puzzle, each tile's <b>color</b> corresponds to the <b>turn</b> in which you solved it, going chromatically from <b>blue</b> on turn 1 to <b>red</b> on turn 5:
                    <div>
    <div class='first-turn inline-square'></div>
                    <div class='second-turn inline-square'></div>
                    <div class='third-turn inline-square'></div>
                    <div class='fourth-turn inline-square'></div>
                    <div class='fifth-turn inline-square'></div></div>`,
                },
            },
        ],
    });
    return postGameDriver;
}

function createAdvancedCarouselOptionsDriver(gameDifficultyLevel: DifficultyLevel, onDestroyedCallback) {
    const postGameDriver = driver({
        allowClose: true,
        stagePadding: 2,
        onDestroyed: onDestroyedCallback,
        popoverClass: 'tutorial-theme',
        steps: [
            {
                element: '.indicator-container',
                popover: {
                    description: `<b>Tip</b>: You can tap the three dots to view all clues at once${
                        gameDifficultyLevel > DifficultyLevel.EASY ? ', or 🔀 to order the clues if you get stuck.' : ''
                    }`,
                },
            },
        ],
    });
    return postGameDriver;
}

function createShareIncentiveDriver() {
    const shareIncentiveDriver = driver({
        allowClose: true,
        stagePadding: 2,
        popoverClass: 'tutorial-theme share-incentive-popover',
        overlayColor: 'var(--tutorial-overlay-color)',
        steps: [
            {
                element: '#share-button',
                popover: {
                    align: 'center',
                    side: 'top',
                    description: 'Share with friends to unlock secret bonus puzzles! 🔓✨',
                    showButtons: [],

                    onPopoverRender: (popover) => {
                        setTimeout(() => {
                            const arrow = popover.arrow;
                            if (arrow) {
                                arrow.classList.remove(
                                    'driver-popover-arrow-align-start',
                                    'driver-popover-arrow-align-end'
                                );
                                arrow.classList.add('driver-popover-arrow-align-center');
                            }
                        }, 0);
                    },
                },
                onHighlighted: (elem) => {
                    elem.addEventListener('click', (e) => {
                        shareIncentiveDriver.destroy();
                    });
                },
            },
        ],
    });
    return shareIncentiveDriver;
}

function createInfoPopUpDriver() {
    const infoDriver = driver({
        allowClose: true,
        stagePadding: 2,
        popoverClass: 'tutorial-theme',
        overlayColor: 'var(--tutorial-overlay-color)',
        steps: [
            {
                element: '#help-icon',
                popover: {
                    align: 'end',
                    side: 'bottom',
                    description: 'Confused? Click here for help!',
                    showButtons: [],
                },
                onHighlighted: (elem) => {
                    elem.addEventListener('click', (e) => {
                        infoDriver.destroy();
                    });
                },
            },
        ],
    });
    return infoDriver;
}

export {
    createPreGameDriver,
    createMidGameDriver,
    createFirstIncorrectTurnDriver,
    createPostGameDriver,
    createAdvancedCarouselOptionsDriver,
    createShareIncentiveDriver,
    createInfoPopUpDriver,
};
